/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { usePathname, useRouter, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useRedirectToSubscribe } from "@packages/kids";
import { useCurrentQueueItem, usePlayer } from "@packages/media";
import type {
  Collection,
  CollectionPrayers,
  TrustedCollection,
} from "@packages/sdk";
import {
  useInfiniteScroll,
  useRedirectToAuth,
  useRequestCollectionPrayers,
  useSession,
} from "@packages/sdk";
import {
  Button,
  cssMediaQueryMinWidthTablet,
  Loader,
  numericPercentages,
  numericPixels,
  spacing,
  TextWithLinks,
  useIsDesktopViewport,
} from "@packages/ui";
import { useSearchParams } from "next/navigation";
import { Suspense, useCallback, useMemo } from "react";

import { CollectionImageHeader } from "../../../../../components/CollectionImageHeader";
import { PrayersList } from "../../../../../components/PrayersList";
import { CollectionQuickActions } from "./CollectionQuickActions";

const columnLeftStyles: Interpolation = {
  alignSelf: "flex-start",
  maxWidth: "100vw",
  minWidth: "256px",
  paddingRight: spacing.none,
  position: "inherit",
  top: "inherit",
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    paddingRight: spacing.l,
    position: "sticky",
    top: "100px",
    width: `calc(${numericPercentages[40]} + ${spacing.l})`,
  },
};

const columnRightStyles: Interpolation = {
  flex: "1",
  paddingBottom: numericPixels[48],
};

const descriptionStyles: Interpolation = {
  height: "fit-content",
  marginBottom: spacing.s,
  marginTop: spacing.m,
  whiteSpace: "pre-line",
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    paddingLeft: spacing.none,
    paddingRight: spacing.none,
  },
};

const detailsContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "column-reverse",
  },
};

const pageStyles: Interpolation = {
  display: "flex",
  paddingBottom: spacing.l,
  flexDirection: "column",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
  },
};

const showAllButtonStyles: Interpolation = {
  margin: `${spacing.m} auto`,
  [cssMediaQueryMinWidthTablet]: {
    margin: `0 0 0 ${spacing.l}`,
  },
};

type Props = {
  collection: TrustedCollection | Collection;
  filtered?: boolean;
  isLoading: boolean;
};

const CollectionContent = ({
  collection,
  filtered = false,
  isLoading = false,
}: Props) => {
  const analytics = useAnalytics();
  const isDesktop = useIsDesktopViewport();
  const { status } = useSession();
  const pathname = usePathname();
  const player = usePlayer();
  const { currentItem } = useCurrentQueueItem();
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useTranslations();
  const redirectToSubscribe = useRedirectToSubscribe();
  const redirectToAuth = useRedirectToAuth();

  const { infiniteQuery: prayerQuery } = useRequestCollectionPrayers({
    id: collection.id,
    enabled: !filtered,
  });

  const { loading: loadingMorePrayers } = useInfiniteScroll({
    query: prayerQuery,
    screenPercentRemaining: 1,
  });

  const isCollectionAvailable = new Date() > new Date(collection.available_at);

  const removeFilter = () => {
    const nextSearchParams = new URLSearchParams(searchParams.toString());
    nextSearchParams.delete("filter");
    router.push(`${pathname}?${nextSearchParams}`);
    router.refresh();
  };

  const handleSubscribe = useCallback(
    () =>
      redirectToSubscribe({
        redirectBackOnSubscription: true,
        forContent: collection
          ? { title: collection.title, images: collection.images }
          : null,
      }),
    [redirectToSubscribe, collection],
  );

  const playCollection = async () => {
    if (status === "authenticated") {
      if (!collection.has_access) {
        analytics.track({
          event: collection.has_video
            ? HallowAnalyticsEvent.TappedSubscribeToWatch
            : HallowAnalyticsEvent.TappedSubscribeToListen,
          properties: {
            screen_name: "collection_detail",
            collection_id: collection.id,
          },
        });
        return handleSubscribe();
      }

      analytics.track({
        event: HallowAnalyticsEvent.TappedPlayNextSession,
        properties: {
          collection: collection.id,
        },
      });
      const playType = collection.is_album ? "collection" : "collection-next";

      await player?.playNew({ type: playType, id: collection.id });
    } else {
      analytics.track({
        event: !collection.has_access
          ? collection.has_video
            ? HallowAnalyticsEvent.TappedSubscribeToWatch
            : HallowAnalyticsEvent.TappedSubscribeToListen
          : collection.has_video
            ? HallowAnalyticsEvent.TappedWatchForFree
            : HallowAnalyticsEvent.TappedListenForFree,
        properties: {
          screen_name: "collection_detail",
          collection_id: collection.id,
        },
      });
      return !collection.has_access ? handleSubscribe() : redirectToAuth();
    }
  };

  const playAll = async (shuffle: boolean = false) => {
    analytics.track({
      event: HallowAnalyticsEvent.TappedPlayAll,
      properties: {
        collection: collection.id,
      },
    });

    if (status === "authenticated") {
      if (!collection.has_access) return handleSubscribe();

      await player?.playNew({ type: "collection", id: collection.id }, shuffle);
    } else {
      return !collection.has_access ? handleSubscribe() : redirectToAuth();
    }
  };

  const playSession = async (id: number) => {
    if (status === "authenticated") {
      if (!collection.has_access) return handleSubscribe();

      const playType = collection.is_album ? "album" : "prayer";
      const targetId = collection.is_album ? collection.id : id;

      if (
        currentItem?.type === "prayer" &&
        currentItem.prayer.id === id &&
        player
      ) {
        if (player.paused) {
          await player.play();
        } else {
          player.pause();
        }
      } else {
        await player?.playNew({
          type: playType,
          id: targetId,
          target: id,
        });
      }
    } else {
      return !collection.has_access ? handleSubscribe() : redirectToAuth();
    }
  };

  const prayers = useMemo(() => {
    if (prayerQuery.data?.pages) {
      return prayerQuery.data.pages.reduce<CollectionPrayers>(
        (acc, page) => acc.concat(page),
        [],
      );
    }
    return collection?.prayers ?? [];
  }, [prayerQuery.data, collection?.prayers]);

  const collectionDescription = useMemo(() => {
    if (!!(collection as TrustedCollection)?.seo_description) {
      return `${collection.desc}\n\n${(collection as TrustedCollection)?.seo_description}`;
    }
    return collection.desc;
  }, [collection?.desc, (collection as TrustedCollection)?.seo_description]);

  return (
    <Suspense>
      <div css={pageStyles}>
        <div css={columnLeftStyles}>
          <CollectionImageHeader
            collection={collection}
            playAll={playAll}
            playCTA={playCollection}
            isLoading={isLoading}
          />
          <div css={detailsContainerStyles}>
            {isDesktop ? (
              <TextWithLinks size="m" css={descriptionStyles} type="body">
                {collectionDescription}
              </TextWithLinks>
            ) : (
              <TextWithLinks
                expandable={true}
                lineClamp={3}
                overflow={"ellipsis"}
                size="m"
                css={descriptionStyles}
                type="body"
              >
                {collectionDescription}
              </TextWithLinks>
            )}
            <CollectionQuickActions
              collection={collection}
              color={collection.images.color_hex}
              id={collection.id}
              image={collection.images.large}
              playAll={playAll}
              sessionsAvailable={isCollectionAvailable}
              text={collection.title}
            />
          </div>
        </div>
        <div css={columnRightStyles}>
          <PrayersList
            playSession={playSession}
            prayers={prayers}
            sessions={collection.sessions}
            sessionsAvailable={isCollectionAvailable}
          />
          {loadingMorePrayers && <Loader />}
          {filtered && (
            <Button onClick={() => removeFilter()} css={showAllButtonStyles}>
              {t("show_full_collection_button")}
            </Button>
          )}
        </div>
      </div>
    </Suspense>
  );
};

export default CollectionContent;
